import { Button, IconButton, Stack, Typography } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import html2canvas from 'html2canvas';
import MUIDataTable, { MUIDataTableOptions } from "mui-datatables";
import pdfMake from "pdfmake/build/pdfmake";
import { useContext, useEffect, useRef, useState } from 'react';
import { downloadExcel } from 'react-export-table-to-excel';
import { useTranslation } from 'react-i18next';
import { FaFilePdf } from 'react-icons/fa';
import { RiFileExcel2Fill } from 'react-icons/ri';
import { toast } from 'react-toastify';
import { UserContext } from '../../context/UserContext';
import AttToken from '../../helpers/attToken';
import { trackEventMatomo } from '../../helpers/matomo';
import Reports from '../../service/Reports';
import { aprovadosType } from '../../types/aprovations/types';
import { ChecklistReportType, ReportTableDownloadRow, ReportTableExportObject, TableObjectRow } from '../../types/reports/types';
import { StyledDiv } from '../TableHelpers/TableStyle';
import ModalDetailPrint from './Modal/ModalDetailPrint';
import ModalExclusion from './ModalExclusion';
import ShareIcon from '@mui/icons-material/Share';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ModalShareReports from './Modal/ModalShareReports';


const reportsService = new Reports();

const defaultColumns = {
  inicio: true,
  fim: true,
  dataAvaliacao: false,
  formulario: true,
  user: true,
  cliente: true,
  local: true,
  conjunto: true,
  unidade: true,
  subItemIdentificador: true,
  identificador: true,
  status: true,
  duracao: false,
  foiDelegado: false,
  idCliente: false,
  nomeEmpresa: false,
  gerencia: false,
  equipes: false,
  schedual: false,
  aprovado: false,
  tempoPausa: false,
  versaoForm: false,
  nomeAprovador: false,
  naoConforme: false,
  conforme: false,
  latitude: true,
  longitude: true,
  distancia: true,
  scorePorcentagem: true,
  nomeTipoServico: false,
  dataEnvio: true,
};

export default function ReportsTableComponent({
  currentItems,
  setPageNumber,
  setItensPerPage,
  pageNumber,
  itensPerPage,
  limit,
}: {
  currentItems: ChecklistReportType[],
  setPageNumber: Function,
  setItensPerPage: Function,
  pageNumber: number,
  itensPerPage: number,
  limit: number,
}) {

  const { t, i18n } = useTranslation('translation');
  const { hierarchyLabel, userData } = useContext(UserContext);

  const mapKeys = {
    inicio: t('Reports.columns.start'),
    fim: t('Reports.columns.end'),
    formulario: t('Reports.columns.form'),
    user: t('profiles.user'),
    cliente: hierarchyLabel.cliente,
    local: hierarchyLabel.local,
    conjunto: hierarchyLabel.conjunto,
    unidade: hierarchyLabel.unidade,
    subItemIdentificador: t('Reports.columns.subIdentifier'),
    identificador: t('Reports.columns.identifier'),
    status: t('Reports.columns.status'),
    duracao: t('Reports.columns.duration'),
    foiDelegado: t('Reports.columns.delegated'),
    idCliente: t('Reports.columns.customerId'),
    nomeEmpresa: t('Reports.columns.companyName'),
    gerencia: hierarchyLabel.gerencia,
    equipes: t('Reports.columns.teams'),
    schedual: t('Reports.columns.schedual'),
    aprovacaoStatus: t('Reports.columns.aprooval'),
    tempoPausa: t('Reports.columns.breakTime'),
    versaoForm: t('Reports.columns.version'),
    nomeAprovador: t('Reports.columns.aprooverName'),
    naoConforme: t('Reports.columns.notAccording'),
    conforme: t('Reports.columns.according'),
    latitude: t('Reports.columns.latitude'),
    longitude: t('Reports.columns.longitude'),
    distancia: t('Reports.columns.distace'),
    scorePorcentagem: 'Score',
    nomeTipoServico: t('Reports.columns.serviceTypeName'),
    dataEnvio: t('Reports.columns.synchronized'),
    dataAvaliacao: 'Data de Aprovação',
  };

  const [columnsViews, setColmnsViews] = useState<{ [k: string]: boolean }>();
  const [openModal, setOpenModal] = useState(false);
  const [openShareModal, setOpenShareModal] = useState(false);
  const [reportsToShare, setReportsToShare] = useState<ChecklistReportType[]>([]);
  const [selectedItem, setSelectedItem] = useState<ChecklistReportType[]>([]);
  const [multipleRowsSelected, setMultipleRowsSelected] = useState<{ user: string, timestamp: number }[]>();
  const [open, setOpen] = useState(false);
  const [toExclude, setToExclude] = useState<{ user: string, timestamp: number }[] | null>(null);
  const [rowsSelected, setRowsSelected] = useState<number[]>([]);


  const printToPdf = () => {
    const table = document.getElementById("print_to_pdf");
    if (table) {
      html2canvas(table)?.then(canvas => {
        const data = canvas?.toDataURL();
        const pdfExportSetting = {
          PageOrientation: 'landscape',
          content: [
            {
              pageSize: 'a4',
              compress: true,
              image: data,
              width: 400,
            }
          ]
        };
        pdfMake?.createPdf(pdfExportSetting)?.download("Relatório Axyma Forms.pdf");
      });
    }
  };
  const [loading, setLoading] = useState(false);

  const getReports = async (ids: { user: string; timestamp: number }[]) => {
    const response = toast.promise(async () => {
      setLoading(true);
      const token = await AttToken();
      if (token) {
        const response = await reportsService.getDetailsById(token, ids);
        if (!response) throw new Error('No response from server');
        if (response?.status === 200) {
          setLoading(false);
          return response?.data?.execucoes;
        } if (response?.status === 204) {
          setLoading(false);
          setToExclude(ids);
          setOpen(true);
        } if ((response?.status !== 200) && (response?.status !== 204)) {
          setLoading(false);
        }
      }
    }
      , {
        pending: t('Reports.ticketList.fetchingInformation'),
        success: t('Reports.ticketList.informationFound'),
        error: t('Reports.ticketList.failed'),
      });
    return response;
  };

  useEffect(() => {
    const localColumnsViews = localStorage.getItem('columnsNotif');

    if (localColumnsViews && localColumnsViews !== 'undefined' && localColumnsViews !== 'null') {
      try {
        const parsed = JSON.parse(localColumnsViews);
        setColmnsViews(parsed || defaultColumns);
      } catch (error) {
        console.error('Erro ao fazer parse das colunas:', error);
        localStorage.setItem('columnsNotif', JSON.stringify(defaultColumns));
        setColmnsViews(defaultColumns);
      }
    } else {
      localStorage.setItem('columnsNotif', JSON.stringify(defaultColumns));
      setColmnsViews(defaultColumns);
    }
  }, []);

  const columns = [
    {
      name: "inicio",
      label: 'ID',
      options: {
        display: false,
        filter: true,
        sort: true,
        sortThirdClickReset: false,
      }
    },
    {
      name: "inicio",
      label: mapKeys.inicio,
      options: {
        display: columnsViews ? columnsViews.inicio : true,
        filter: false,
        sort: true,
        sortThirdClickReset: true,
        customBodyRender: (value: number) => {
          const data = new Date(value)?.toLocaleString(i18n.language === 'en' ? 'en-US' : 'pt-BR');
          return data;
        },
      }
    },
    {
      name: 'fim',
      label: mapKeys.fim,
      options: {
        display: columnsViews ? columnsViews.fim : true,
        filter: false,
        sort: true,
        sortThirdClickReset: true,
        customBodyRender: (value: number) => {
          const data = typeof value === 'number' ? new Date(value).toLocaleString(i18n.language === 'en' ? 'en-US' : 'pt-BR') : 'Formulário pausado';
          return data;
        },
      }
    },
    {
      name: 'dataEnvio',
      label: mapKeys.dataEnvio,
      options: {
        display: columnsViews ? columnsViews.fim : true,
        filter: false,
        sort: true,
        sortThirdClickReset: true,
        customBodyRender: (value: number) => {
          const data = new Date(value).toLocaleString(i18n.language === 'en' ? 'en-US' : 'pt-BR');
          return data === "Invalid Date" ? "" : data;
        },
      }
    },
    {
      name: 'formulario',
      label: mapKeys.formulario,
      options: {
        display: columnsViews ? columnsViews.formulario : true,
        filter: true,
        sort: true,
        sortThirdClickReset: true,
        customBodyRender: (value: string) => {
          return <p style={{ width: '200px', wordBreak: 'break-all' }} >{value}</p>;

        },
      }
    },
    {
      name: 'scorePorcentagem',
      label: 'Score',
      options: {
        display: columnsViews ? columnsViews?.scorePorcentagem : true,
        filter: true,
        sort: true,
        sortThirdClickReset: true,
        customBodyRender: (value: string) => {
          return <p>{value}</p>;

        },
      }
    },
    {
      name: 'user',
      label: mapKeys.user,
      options: {
        display: columnsViews ? columnsViews.user : true,
        filter: true,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: 'cliente',
      label: mapKeys.cliente,
      options: {
        display: columnsViews ? columnsViews.cliente : true,
        filter: true,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: "local",
      label: mapKeys.local,
      options: {
        display: columnsViews ? columnsViews.local : true,
        filter: true,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: "conjunto",
      label: mapKeys.conjunto,
      options: {
        display: columnsViews ? columnsViews.conjunto : true,
        filter: true,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: "unidade",
      label: mapKeys.unidade,
      options: {
        display: columnsViews ? columnsViews.unidade : true,
        filter: true,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: "subitemIdentificador",
      label: mapKeys.subItemIdentificador,
      options: {
        display: (columnsViews ? columnsViews.subItemIdentificador : true),
        filter: true,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: "identificador",
      label: mapKeys.identificador,
      options: {
        display: (columnsViews ? columnsViews.identificador : true),
        filter: true,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: "status",
      label: mapKeys.status,
      options: {
        display: (columnsViews ? columnsViews.status : true),
        filter: true,
        sort: true,
        sortThirdClickReset: true,
        customBodyRender: (value: boolean) => {
          return value ? <span className='tag is-primary'>{t('active')}</span> : <span className='tag is-danger'>{t('Reports.ticketList.canceled')}</span>;
        },
      }
    },
    {
      name: "duracao",
      label: mapKeys.duracao,
      options: {
        display: (columnsViews ? columnsViews.duracao : false),
        filter: true,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: "foiDelegado",
      label: mapKeys.foiDelegado,
      options: {
        display: (columnsViews ? columnsViews.foiDelegado : false),
        filter: true,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: "idCliente",
      label: mapKeys.idCliente,
      options: {
        display: (columnsViews ? columnsViews.idCliente : false),
        filter: true,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: "nomeEmpresa",
      label: mapKeys.nomeEmpresa,
      options: {
        display: (columnsViews ? columnsViews.nomeEmpresa : false),
        filter: true,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: "gerencia",
      label: mapKeys.gerencia,
      options: {
        display: (columnsViews ? columnsViews.gerencia : false),
        filter: true,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: "schedual",
      label: mapKeys.schedual,
      options: {
        display: (columnsViews ? columnsViews.schedual : false),
        filter: true,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: "aprovacaoStatus",
      label: mapKeys.aprovacaoStatus,
      options: {
        display: (columnsViews ? columnsViews.aprovacaoStatus : false),
        filter: true,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: "dataAvaliacao",
      label: mapKeys.dataAvaliacao,
      options: {
        display: (columnsViews ? columnsViews.dataAvaliacao : false),
        filter: true,
        sort: true,
        sortThirdClickReset: true,
        customBodyRender: (value: number) => {
          if (typeof value !== 'number' || isNaN(value)) return '';
          const data = new Date(value)?.toLocaleString(i18n.language === 'en' ? 'en-US' : 'pt-BR');
          return data;
        },
      }
    },
    {
      name: "tempoPausa",
      label: mapKeys.tempoPausa,
      options: {
        display: (columnsViews ? columnsViews.tempoPausa : false),
        filter: true,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: "versaoForm",
      label: mapKeys.versaoForm,
      options: {
        display: (columnsViews ? columnsViews.versaoForm : false),
        filter: true,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: "nomeAprovador",
      label: mapKeys.nomeAprovador,
      options: {
        display: (columnsViews ? columnsViews.nomeAprovador : false),
        filter: false,
        sort: false,
        sortThirdClickReset: false,
        customBodyRender: (value: aprovadosType[]) => {
          return (
            value?.map((aprov) => (
              <Typography fontSize={13}>
                {aprov.username},
              </Typography>
            ))
          );
        }
      }
    },
    {
      name: "naoConforme",
      label: mapKeys.naoConforme,
      options: {
        display: (columnsViews ? columnsViews.naoConforme : false),
        filter: true,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: "conforme",
      label: mapKeys.conforme,
      options: {
        display: (columnsViews ? columnsViews.conforme : false),
        filter: true,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: "latitude",
      label: mapKeys.latitude,
      options: {
        display: (columnsViews ? columnsViews.latitude : false),
        filter: true,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: "longitude",
      label: mapKeys.longitude,
      options: {
        display: (columnsViews ? columnsViews.longitude : false),
        filter: true,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: "distancia",
      label: mapKeys.distancia,
      options: {
        display: (columnsViews ? columnsViews.distancia : false),
        filter: true,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: "nomeTipoServico",
      label: mapKeys.nomeTipoServico,
      options: {
        display: (columnsViews ? columnsViews.nomeTipoServico : false),
        filter: true,
        sort: true,
        sortThirdClickReset: true
      }
    },
  ];

  const translatedTextLabels: MUIDataTableOptions = {
    viewColumns: true,
    onViewColumnsChange: (changedColumn: string, action: string) => {
      switch (changedColumn) {
        case 'latitude':
          if (action === 'add') {
            return setColmnsViews({
              longitude: true,
              distancia: true,
            });
          } else {
            return setColmnsViews({
              longitude: false,
              distancia: false,
            });
          }
        case 'longitude':
          if (action === 'add') {
            return setColmnsViews({
              latitude: true,
              distancia: true,
            });
          } else {
            return setColmnsViews({
              latitude: false,
              distancia: false,
            });
          }
        case 'distancia': {
          if (action === 'add') {
            return setColmnsViews({
              latitude: true,
              longitude: true,
            });
          } else {
            return setColmnsViews({
              latitude: false,
              longitude: false,
            });
          }
        }
      }
      if (action === 'remove') {
        try {
          const newColumnsState = {
            ...columnsViews,
            [changedColumn]: false,
          };
          setColmnsViews(newColumnsState || defaultColumns);
          localStorage.setItem('columnsNotif', JSON.stringify(newColumnsState || defaultColumns));
        } catch (error) { }
      } else {
        try {
          const newColumnsState = {
            ...columnsViews,
            [changedColumn]: true
          };
          setColmnsViews(newColumnsState || defaultColumns);
          localStorage.setItem('columnsNotif', JSON.stringify(newColumnsState || defaultColumns));
        } catch (error) { }
      }
    },
    textLabels: {
      selectedRows: {
        text: `${t('Reports.selectedForms')}`,
        delete: `Abrir`,
      },
      body: {
        noMatch: `${t('table.noMatch')}`,
        toolTip: `${t('table.toolTip')}`,
        columnHeaderTooltip: column =>
          `${t('table.columnHeaderTooltip')} ${column.label}`
      },
      pagination: {
        next: `${t('table.next')}`,
        previous: `${t('table.previous')}`,
        rowsPerPage: `${t('table.rowsPerPage')}`,
        displayRows: `${t('table.displayRows')}`
      },
      toolbar: {
        search: `${t('table.search')}`,
        filterTable: `${t('table.filterTable')}`,
        viewColumns: `${t('Aprovations.showHideColumns')}`
      },
      filter: {
        title: `${t('table.title')}`,
        reset: `${t('table.reset')}`,
      },
      viewColumns: {
        title: `${t('table.viewColumnsTitle')}`,
      }
    },
    onChangePage: (currentPage: number) => {
      setPageNumber(currentPage);
    },
    onChangeRowsPerPage: (numberOfRows: number) => {
      setPageNumber(0);
      setItensPerPage(numberOfRows);
    },
    serverSide: true,
    page: pageNumber,
    rowsPerPage: itensPerPage,
    count: limit,
    search: false,
    sort: false
  };
  const getMuiTheme = () => createTheme({
    components: {
      MuiPaper: {
        styleOverrides: {
          elevation: {
            padding: 2
          }
        }
      },
      MuiFormGroup: {
        styleOverrides: {
          root: {
            "&>:nth-child(1)": {
              display: 'none'
            }
          }
        }
      },
      MuiInputBase: {
        styleOverrides: {
          input: {
            width: 'fit-content',
            margin: 0,
            padding: 0
          }
        }
      },
      MuiList: {
        styleOverrides: {
          root: {
            display: 'flex',
            flexDirection: 'column',
          }
        }
      },
      MuiTableBody: {
        styleOverrides: {
          root: {
            td: {
              pointerEvents: `${loading ? 'none' : 'auto'}`,
              backgroundColor: `${loading ? '#eeeeee' : 'white'}`,
              height: 'fit-content',
              margin: 0,
              padding: '3px 10px',
              width: 'fit-content'
            },
          }
        }
      },
      MuiGrid: {
        styleOverrides: {
          root: {
            margin: 0,
            padding: 0
          }
        }
      },
      MuiFormControl: {
        styleOverrides: {
          root: {
            padding: '10px'
          }
        }
      },
    },
  });

  const limite = userData.selectExecutionsLimit ?? 25;

  const tableRef = useRef(null);

  const tableOptions: MUIDataTableOptions = {
    rowsSelected: rowsSelected as [],
    selectableRowsHeader: false,
    onRowSelectionChange(_currentRowsSelected, allRowsSelected) {
      trackEventMatomo('Relatórios', 'click', 'row', 'seleciona form');

      if (allRowsSelected.length > limite) {
        toast.info(
          `Você pode selecionar até ${limite} relatórios por vez`,
          { toastId: 'rel' }
        );
        const limitedSelection = allRowsSelected.slice(0, limite);
        const selectedIndexes = limitedSelection.map(row => row.dataIndex);

        setRowsSelected(selectedIndexes);
        return;
      }

      const selectedIndexes = allRowsSelected.map(row => row.dataIndex);
      setRowsSelected(selectedIndexes);

      const idsList: { user: string; timestamp: number }[] = [];

      selectedIndexes.forEach(index => {
        const form = currentItems[index];
        if (form) {
          idsList.push({
            user: form?.username as string,
            timestamp: Number(form.inicio),
          });
        }
      });

      setMultipleRowsSelected(idsList);
    },
    isRowSelectable: (dataIndex: number) => {
      if (rowsSelected.includes(dataIndex)) return true;
      return rowsSelected.length < limite;
    }
    ,
    customToolbarSelect: () => {
      return (
        <Stack direction='row' spacing={1} alignItems='center' padding={1}>
          <Button
            color='secondary'
            variant='contained'
            endIcon={<OpenInNewIcon />}
            disabled={loading}
            onClick={async () => {
              trackEventMatomo('Relatórios', 'click', 'button', 'abre form');
              if (multipleRowsSelected && multipleRowsSelected.length) {
                const forms = await getReports(multipleRowsSelected);
                if (forms && forms.length) {
                  setSelectedItem(forms);
                  setOpenModal(true);
                }
              }
            }}>
            {t('Reports.openForms')}
          </Button>
          <Button
            variant='contained'
            endIcon={<ShareIcon />}
            disabled={loading}
            onClick={() => {
              trackEventMatomo('Relatórios', 'click', 'button', 'compartilha relatórios');
              const selectedReports = rowsSelected
                .map((index) => currentItems[index])
                .filter((report): report is ChecklistReportType => Boolean(report));
              if (selectedReports.length) {
                setReportsToShare(selectedReports);
                setOpenShareModal(true);
              }
            }}
          >
            Compartilhar
          </Button>
        </Stack>
      );
    },
    onRowClick: async (rowData) => {
      if (rowsSelected.length >= limite) {
        return;
      }
      trackEventMatomo('Relatórios', 'click', 'button', 'abre form');
      const selected = currentItems?.find(e => e.inicio === Number(rowData[0]));
      if (selected) {
        const idsList: { user: string, timestamp: number }[] = [{
          user: selected?.username as string,
          timestamp: Number(selected.inicio)
        }];
        const forms = await getReports(idsList);
        if (forms && forms.length) {
          setSelectedItem(forms);
          setOpenModal(true);
        }
      }
    },
    customToolbar: () => {
      return (
        <>
          {open && <ModalExclusion
            open={open}
            setOpen={setOpen}
            toExclude={toExclude}
            setToExclude={setToExclude} />}
          <IconButton
            size='small'
            onClick={() => {
              trackEventMatomo('Relatórios', 'click', 'button', 'gera PDF');
              printToPdf();
            }}>
            <FaFilePdf />
          </IconButton>
          <IconButton
            size='small'
            onClick={() => {
              trackEventMatomo('Relatórios', 'click', 'button', 'gera csv');
              const current = currentItems as ChecklistReportType[];
              const bodyArray: ReportTableExportObject[] = [];
              const headerArray: string[] = [];
              const mapKeyList = Object.keys(mapKeys) as Array<keyof typeof mapKeys>;
              mapKeyList?.forEach(key => {
                if (columnsViews && columnsViews[key]) {
                  headerArray.push(mapKeys[key as keyof typeof mapKeys]);
                }
              });
              current?.forEach((elem: ChecklistReportType) => {
                const objToInstert: ReportTableExportObject = {};
                const elemRecord = elem as Record<string, unknown>;
                mapKeyList?.forEach((key) => {
                  if (columnsViews && columnsViews[key]) {
                    if (key === 'inicio' || key === 'fim') {
                      objToInstert[key] = new Date(Number(elemRecord[key])).toLocaleString('pt-br').replace(',', ' ');
                    } else if (key === 'status') {
                      objToInstert[key] = elemRecord[key] ? 'Ativo' : 'Inativo';
                    } else if (key === 'equipes' && elemRecord[key]) {
                      let teamNamesString = '';
                      const teams = elemRecord[key] as Record<string, { nome: string }>;
                      Object.keys(teams)?.forEach(teamKey => {
                        const team = teams[teamKey];
                        if (team) {
                          teamNamesString += team.nome + ' | ';
                        }
                      });
                      objToInstert[key] = teamNamesString.slice(0, teamNamesString.length - 3);
                    } else {
                      objToInstert[key] = elemRecord[key];
                    }
                  }
                });
                bodyArray.push(objToInstert);
              });
              downloadExcel({
                fileName: "Relatório XLSX Axyma Forms",
                sheet: "react-export-table-to-excel",
                tablePayload: {
                  header: headerArray || [''],
                  body: bodyArray as unknown as TableObjectRow[]
                },
              });
            }}>
            <RiFileExcel2Fill />
          </IconButton>
        </>
      );
    },
    downloadOptions: {
      filename: 'Relatório CSV Axyma Forms',
    },
    onDownload: (buildHead, buildBody, columns, data) => {
      const newData = data.map((ele: ReportTableDownloadRow) => {
        ele.data[0] = new Date(ele.data[0] as string | number | Date).toLocaleString('pt-br').replace(',', '-');
        ele.data[1] = new Date(ele.data[1] as string | number | Date).toLocaleString('pt-br').replace(',', '-');
        return ele;
      });
      return "\uFEFF" + buildHead(columns) + buildBody(newData);
    },
    viewColumns: false,
    print: true,
    download: true,
    filter: false,
    filterType: 'textField',
    responsive: `vertical`,
    rowsPerPageOptions: [10, 15, 50, 100, 500, 1000],
  };

  return (
    <>
      {openModal &&
        <ModalDetailPrint
          openModal={openModal}
          setOpenModal={setOpenModal}
          selected={selectedItem as ChecklistReportType[]}
        />}
      <ModalShareReports
        open={openShareModal}
        onClose={() => setOpenShareModal(false)}
        reports={reportsToShare}
      />
      <div style={{
        justifyContent: 'center',
        maxWidth: '100%',
      }}>
        <StyledDiv
          theme={JSON.stringify(window.screen.width)}
          id="print_to_pdf" style={{ paddingBottom: '50px' }}>
          <ThemeProvider theme={getMuiTheme()}>
            <div ref={tableRef} id={'myTable'}>
              <MUIDataTable
                title={`${t('sidebar.report')}`}
                data={currentItems}
                columns={columns}
                options={{ ...tableOptions, ...translatedTextLabels }}
              />
            </div>
          </ThemeProvider>
        </StyledDiv>
      </div >
    </>
  );
}
